/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// Import self-hosted Sen font as a module (Vite will handle bundle paths)
import senVariable from '@fonts/sen-variable.woff2?url';

// Import Ashley brand fonts as modules (must use JS imports to work with MRT bundle paths)
import chesnaRegular from '@fonts/ChesnaGrotesk/chesnagrotesk-regular.woff2?url';
import chesnaRegularItalic from '@fonts/ChesnaGrotesk/chesnagrotesk-regularitalic.woff2?url';
import chesnaSemibold from '@fonts/ChesnaGrotesk/chesnagrotesk-semibold.woff2?url';
import chesnaSemiboldItalic from '@fonts/ChesnaGrotesk/chesnagrotesk-semibolditalic.woff2?url';
import publicoRoman from '@fonts/PublicoHeadline/publicoheadline-roman.woff2?url';
import publicoItalic from '@fonts/PublicoHeadline/publicoheadline-italic.woff2?url';

/**
 * Loads self-hosted Sen fonts using the FontFace API.
 * This approach is required for GDPR compliance (no external requests to Google Fonts)
 * and works with Managed Runtime's bundle path system.
 *
 * Uses 'optional' for optimal performance - prevents layout shifts and reduces TBT.
 * Font either loads within ~100ms or fallback is used permanently (no late swaps).
 */
export function loadFonts() {
    if (typeof window === 'undefined' || typeof FontFace === 'undefined') {
        return;
    }

    // Use 'optional' for all contexts to optimize TBT and prevent layout shifts
    const display = 'optional';

    const fonts = [
        new FontFace('Sen', `url(${senVariable})`, { weight: '400 700', style: 'normal', display }),
        new FontFace('Chesna Grotesk', `url(${chesnaRegular})`, { weight: '400', style: 'normal', display }),
        new FontFace('Chesna Grotesk', `url(${chesnaRegularItalic})`, { weight: '400', style: 'italic', display }),
        new FontFace('Chesna Grotesk', `url(${chesnaSemibold})`, { weight: '600', style: 'normal', display }),
        new FontFace('Chesna Grotesk', `url(${chesnaSemiboldItalic})`, { weight: '600', style: 'italic', display }),
        new FontFace('Publico Headline', `url(${publicoRoman})`, { weight: '400', style: 'normal', display }),
        new FontFace('Publico Headline', `url(${publicoItalic})`, { weight: '400', style: 'italic', display }),
    ];

    // Load fonts and add to document
    fonts.forEach((font) => {
        font.load()
            .then((loadedFont) => {
                document.fonts.add(loadedFont);
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to load font:', error);
            });
    });
}

// Auto-load fonts when this module loads on the client
// This runs immediately, not waiting for React hydration
if (typeof window !== 'undefined') {
    loadFonts();
}
